import { buildRadioCheck, icon, node } from "~/util/dom";
import { gpxLog } from "~/gpx/util";
import Dialog from "~/ui/dialog";
import { makeEvent } from "~/lib/signals";
import ContentOverlay from "~/content.overlay";
import GPXItem from "~/gpx/item";
import GPXImport from "~/gpx/import";
import { _ } from "~/i18n";
import Scene from "~/scene";
import { store } from "~/state/store";
import { setCoordinates } from "~/state/map/state";
import { IImportResponse, IImportTrack } from "~/es6/provider-gpx";
import GPXSave from "~/ui/dialogs/gpx-save";

const GEOM_RED_COLOR = "#f00";
const GEOM_GRAY_COLOR = "#6b7580";
const MAX_ZOOM = 17;
const HIDE_CLASS = "hide";

/**
 * @class GPX
 *
 * gpx-activated
 * gpx-deactivated
 */
export default class GPXContent extends ContentOverlay {
	_hasData: boolean;

	_folderId: string;

	_folderTitle: string;

	_layers: {
		markers: any;
		geometry: any;
		free: any;
	};

	_allZoomCoords: Array<any>;

	_items: Array<GPXItem>;

	_itemsDiff: {
		[key: string]: any;
	};

	// FIXME: nejak pretizit deklaraci _dom z rodice, aby to bral nejen VSC, ale aby to slo taky prelozit - pridat declare vadi prekladu, i kdyz VSC je s tim ok
	// @ts-ignore
	_dom: {
		container: HTMLDivElement;
		list?: HTMLUListElement;
		overview?: HTMLDivElement;
		loader?: HTMLDivElement;
		saveBtn?: HTMLButtonElement;
		listControl?: HTMLDivElement;
		addBtn?: HTMLButtonElement;
		removeBtn?: HTMLButtonElement;
		allRch?: ReturnType<typeof buildRadioCheck>;
		btnClose?: HTMLButtonElement;
		fileName?: HTMLHeadingElement;
		newImport?: HTMLButtonElement;
	};

	constructor() {
		super();

		this._contentName = "gpx";
		this._dom = {
			container: node("div", { id: "gpx" }),
		};
		this._buildCloseButton();
		this._hasData = false;
		this._folderId = "";
		this._folderTitle = "";
		this._items = [];
		this._itemsDiff = {};
		this._layers = {
			markers: Scene.createLayerMarker("gpx-marker"),
			geometry: Scene.createLayerGeometry("gpx-geom"),
			free: null,
		};
		this._allZoomCoords = [];

		Scene.addListener("provider-changed", event => {
			const data = event.data;

			if (this.isActive() && data.name === "m3d") {
				this._show3dRoutes();
			}
		}, this);

		// dom + list
		this._build();
	}

	activate() {
		if (this.isActive()) {
			return this;
		}

		// prvni cary, pak az body
		Scene.addLayer(this._layers.geometry).enable();
		Scene.addLayer(this._layers.markers).enable();
		Mapy.getComponent("userMarks").deactivate();
		Mapy.getComponent("distanceMeter").deactivate();
		makeEvent("gpx-activated", this);
		Scene.redraw();
		Mapy.SEO.set(_("gpx.content.title"));

		for (const item of this._items) {
			item.visibility = true;
		}

		return super.activate();
	}

	deactivate() {
		if (!this.isActive()) {
			return this;
		}

		this._layers.geometry.disable();
		this._layers.markers.disable();
		Scene.removeLayer(this._layers.geometry);
		Scene.removeLayer(this._layers.markers);
		this._removeFreeLayers();
		makeEvent("gpx-deactivated", this);

		for (const item of this._items) {
			item.visibility = false;
		}

		return super.deactivate();
	}

	hasData() {
		return this._hasData;
	}

	show() {
		Mapy.getComponent("layout").setWidth(1);

		return super.show();
	}

	hide() {
		return super.hide();
	}

	// eslint-disable-next-line class-methods-use-this
	setBottomPanel() {
		const bottomPanel = node("div", { className: "bp-gpx" });
		const content = node("span", { className: "content" });
		const h4 = node("h4", { className: "title", textContent: _("gpx.content.title") });
		const desc = node("p", { className: "desc", textContent: GPXImport.filesName });

		bottomPanel.appendChild(icon("import"));
		bottomPanel.appendChild(content);
		content.appendChild(h4);
		content.appendChild(desc);

		Mapy.getComponent("layout").setBottomPanel(bottomPanel);
	}

	setState(state: {
		gpxData?: IImportResponse;
		[key: string]: any;
	}) {
		if (state && state.gpxData) {
			this.activate();
			this.clear();

			this._folderId = state.folderId || "";
			this._folderTitle = state.folderTitle || _("detail.content.mymapsplace.saved2");
			this._set(state.gpxData);
		}
	}

	addData(gpxData: IImportResponse) {
		if (gpxData && this.isActive()) {
			this.clear(true);
			this._set(gpxData);
		}
	}

	// eslint-disable-next-line class-methods-use-this
	getState() {
		return {};
	}

	itemClick(id: string) {
		const selItem = this._items.filter(item => item.id === id)[0];

		if (selItem) {
			this.leave(selItem);
			this.click(selItem);
		}
	}

	itemHover(id: string, isHover?: boolean) {
		const selItem = this._items.filter(item => item.id === id)[0];

		if (!selItem) {
			return;
		}

		if (isHover) {
			this.enter(selItem);
		} else {
			this.leave(selItem);
		}
	}

	clear(dontClearDiff = false) {
		while (this._items.length) {
			const item = this._items.pop();

			this.remove(item, true);
		}
		this._dom.saveBtn.disabled = true;

		if (!dontClearDiff) {
			this._itemsDiff = {};
		}

		this._updateSelected();
		this._removeFreeLayers();
		this._updateCount();

		this._dom.fileName.textContent = "";
		this._dom.allRch.input.checked = true;
		this._dom.saveBtn.disabled = true;
		this._dom.list.innerHTML = "";
		this._hasData = false;
		this._dom.listControl.classList.add(HIDE_CLASS);
		this._allZoomCoords.length = 0;
	}

	handleEvent(event: Event) {
		switch (event.type) {
			case "click":
				if (this._dom.btnClose.contains(event.target as Node)) {
					const dialog = new Dialog({
						className: "import-gpx-dialog",
						title: _("gpx.content.closeTitle2"),
						desc: _("gpx.content.closeDesc"),
						logSection: "import",
					});

					dialog.setBasic({
						closeTxt: "close2",
						bindEnter: true,
						cb: () => {
							gpxLog("import_delete_confirm");
							GPXImport.clear();
							this.clear();
							this.deactivate();
						},
					});
					if (dialog.dom.desc) {
						dialog.dom.desc.classList.add("no-italic");
					}
					dialog.open();
				} else if (this._dom.saveBtn.contains(event.target as Node)) {
					// dialog
					gpxLog("click", {
						text: "uložit_hvězda",
						type: "import",
					});
					this._createSaveDialog();
				} else if (this._dom.addBtn.contains(event.target as Node)) {
					GPXImport.open({
						folderId: this._folderId,
						folderTitle: this._folderTitle,
						skipAddDialog: true,
					});
				} else if (this._dom.newImport.contains(event.target as Node)) {
					gpxLog("click", {
						text: "Nový import",
					});

					// pokud mame nactene soubory - dialog
					const filesName = GPXImport.filesName;

					if (filesName) {
						gpxLog("import_overwrite_dialog");
						const dialog = new Dialog({
							className: "import-gpx-dialog",
							title: _("gpx.content.newImport2"),
							logSection: "import",
							desc: filesName,
						});

						dialog.setBasic({
							bindEnter: true,
							cb: () => {
								this.clear();
								gpxLog("import_overwrite_confirm");
								GPXImport.clear();
								GPXImport.open({
									folderId: this._folderId,
									folderTitle: this._folderTitle,
								});
							},
							closeTxt: "replace",
						});
						dialog.open();
					} else {
						this.clear();
						GPXImport.clear();
						GPXImport.open({
							folderId: this._folderId,
							folderTitle: this._folderTitle,
						});
					}
				} else if (this._dom.removeBtn.contains(event.target as Node)) {
					const selectedItems = this._getSelectedItems();

					if (selectedItems.length) {
						const dialog = new Dialog({
							className: "import-gpx-dialog",
							title: _("gpx.content.removeSelected2"),
							logSection: "import",
						});

						dialog.setBasic({
							bindEnter: true,
							cb: () => {
								selectedItems.forEach(item => {
									this.remove(item);
									this.diffChange(item);
								});
								gpxLog("import_delete_items");
								this._setSaveBtn();
							},
							closeTxt: "delete",
						});
						dialog.open();
					}
				}
				break;

			case "change":
				if (this._dom.allRch.input === event.target) {
					this._items.forEach(item => {
						item.checked = (event.target as HTMLInputElement).checked;
					});
				}

				this._updateSelected();
				this._setSaveBtn();
				break;

			default:
				//
		}
	}

	enter(item) {
		item.active = true;

		this._items.forEach(arrayItem => {
			const isActive = arrayItem === item;

			arrayItem.markers.forEach(marker => {
				marker.setActive(isActive);
			});
			arrayItem.geometries.forEach(geom => {
				geom.setOptions({
					color: isActive ? GEOM_RED_COLOR : GEOM_GRAY_COLOR,
				});
			});
			arrayItem.setWasmActive(isActive);
		});
	}

	leave(item) {
		item.active = false;

		this._items.forEach(arrayItem => {
			arrayItem.markers.forEach(marker => {
				marker.setActive(true);
			});
			arrayItem.geometries.forEach(geom => {
				geom.setOptions({
					color: GEOM_RED_COLOR,
				});
			});
			arrayItem.setWasmActive(true);
		});
	}

	setChecked(id: string, checked: boolean) {
		const selectedItem = this._items.filter(item => item.id === id)[0];

		if (!selectedItem) {
			return;
		}

		this._items.forEach(arrayItem => {
			if (arrayItem === selectedItem) {
				arrayItem.checked = checked;
			}
		});
	}

	isChecked(id: string): boolean {
		const selectedItem = this._items.filter(item => item.id === id)[0];

		return selectedItem ? selectedItem.checked : false;
	}

	remove(item, noRemove = false) {
		if (!noRemove) {
			const ind = this._items.indexOf(item);

			if (ind !== -1) {
				this._items.splice(ind, 1);
			}
		}

		// upravit poradi markeru
		while (item.markers.length) {
			const marker = item.markers.pop();

			this._layers.markers.removeMarker(marker);
		}
		item.geometries.forEach(geom => {
			this._layers.geometry.removeSmartGeometry(geom);
		});

		item.destroy();

		this._dom.listControl.classList[this._items.length > 1 ? "remove" : "add"](HIDE_CLASS);

		if (!this._items.length) {
			this._hasData = false;
		}

		this._updateSelected();
		this._updateCount();
		this._setSaveBtn();
	}

	change() {
		this._updateSelected();
		this._setSaveBtn();
	}

	// zmena typu - aktivita/cesta
	changeType() {
		this._updateCount();
	}

	diffChange(item) {
		// ulozime si diff
		const itemDiff = structuredClone(item.diff);

		this._itemsDiff[itemDiff.id] = itemDiff;
	}

	click(item) {
		// zoom na mape
		if (item.geometries.length) {
			// zoom mapy podle dat
			const centerZoom = Scene.computeCenterZoom(item.geometryCoords);

			this._setCenterZoom(centerZoom[0], Math.min(centerZoom[1], MAX_ZOOM));
		} else {
			this._setCenterZoom(item.coords[0], MAX_ZOOM);
		}
		// scroll na element vpravo
		const scrollEl = Mapy.getComponent("layout").getScrollNode();
		let height = this._dom.listControl.offsetHeight;
		const allItems = Array.from(this._dom.list.children) as Array<HTMLElement>;

		allItems.every((el, ind) => {
			if (el === item.container) {
				height += ind * el.offsetHeight;

				return false;
			}

			return true;
		});
		scrollEl.scrollTop = height;
	}

	_build() {
		// hlavicka
		const header = node("div", { className: "header" });
		const btnClose = node("button", { className: "btn-close" });
		const title = node("h2", { className: "title", textContent: _("gpx.content.title") });
		const fileName = node("h3", { className: "file-name" });
		// prehled bodu, tras...
		const overview = node("div", { className: "overview" });
		// ulozit a novy import
		const controls = node("div", { className: "controls" });
		const saveBtn = node("button", { className: "btn-save" });
		const saveBtnCaption = node("span", { className: "txt" });

		btnClose.appendChild(icon("close"));
		btnClose.addEventListener("click", this);
		header.appendChild(btnClose);
		header.appendChild(title);
		header.appendChild(fileName);
		saveBtnCaption.appendChild(icon("star-line"));
		saveBtnCaption.appendChild(document.createTextNode(_("gpx.content.save")));
		saveBtn.appendChild(saveBtnCaption);
		saveBtn.appendChild(node("span", { className: "bubble", textContent: _("gpx.content.saveBubble") }));
		saveBtn.addEventListener("click", this);
		saveBtn.setAttribute("data-log-ignore", "true");
		controls.appendChild(saveBtn);
		const addBtn = node("button", { className: "btn-add" });

		addBtn.appendChild(icon("add-circle"));
		addBtn.appendChild(node("span", { className: "txt", textContent: _("gpx.content.addFile") }));
		addBtn.setAttribute("data-log-ignore", "true");
		addBtn.addEventListener("click", this);
		controls.appendChild(addBtn);
		const newImport = node("button", { className: "btn-new-import" });

		newImport.appendChild(icon("reload"));
		newImport.appendChild(node("span", { className: "txt", textContent: _("gpx.content.new") }));
		newImport.setAttribute("data-log-ignore", "true");
		newImport.addEventListener("click", this);
		controls.appendChild(newImport);

		// seznam polozek
		const listArea = node("div", { className: "list-area" });
		const listControl = node("div", { className: "list-control" });
		// pocet a checkbox
		const countInfo = node("span", { className: "count-info" });
		const allRch = buildRadioCheck("checkbox", "all");

		countInfo.appendChild(allRch.label);
		allRch.input.addEventListener("change", this);
		listControl.appendChild(countInfo);
		// smazat vybrane
		const removeBtn = node("button", { className: "btn-remove" });

		removeBtn.appendChild(icon("delete"));
		removeBtn.appendChild(node("span", { className: "txt", textContent: _("gpx.content.delete") }));
		removeBtn.addEventListener("click", this);
		removeBtn.setAttribute("data-log-ignore", "true");
		listControl.appendChild(removeBtn);
		const list = node("ul", { className: "list" });

		header.appendChild(overview);
		header.appendChild(controls);
		listArea.appendChild(listControl);
		listArea.appendChild(list);
		this._dom.container.appendChild(header);
		this._dom.container.appendChild(listArea);
		Object.assign(this._dom, { fileName, btnClose, saveBtn, newImport, list, allRch, removeBtn, listControl, overview, addBtn });
	}

	// eslint-disable-next-line class-methods-use-this
	_createOverviewItem(name = "", value = 0, svg = "", textKey) {
		// obal
		const item = node("div", { className: "overview-item" });
		const iconHolder = node("span", { className: "icon-holder" });

		iconHolder.appendChild(icon(svg));
		const textHolder = node("span", { className: "text-holder" });
		const valueEl = node("span", { className: "value", textContent: value.toString() });
		const text = node("span", { className: "text", textContent: _(textKey, { NUM: value }) });

		item.appendChild(iconHolder);
		item.appendChild(textHolder);
		textHolder.appendChild(valueEl);
		textHolder.appendChild(text);
		// info
		iconHolder.appendChild(node("span", { className: "bubble", textContent: _(`gpx.content.${name}Bubble`) }));

		return item;
	}

	_set(gpxData: IImportResponse) {
		this._hasData = true;
		this._dom.fileName.textContent = GPXImport.filesName;
		this._dom.allRch.input.checked = true;
		this._dom.saveBtn.disabled = false;
		this._dom.list.innerHTML = "";
		this._allZoomCoords.length = 0;

		this._createItems(gpxData);
		// update polozek
		this._updateCount();
		// 3d trasy
		this._show3dRoutes();
		// chceme pouze pro 2..vice itemu
		this._dom.listControl.classList[this._items.length <= 1 ? "add" : "remove"](HIDE_CLASS);
		this._updateSelected();
		this._setSaveBtn();
		// zoom mapy podle dat
		const centerZoom = Scene.computeCenterZoom(this._allZoomCoords);

		this._setCenterZoom(centerZoom[0], Math.min(centerZoom[1], MAX_ZOOM));
	}

	_updateCount() {
		const allItems = {
			points: 0,
			paths: 0,
			activities: 0,
		};

		this._dom.overview.className = "overview";
		this._dom.overview.innerHTML = "";

		if (this._items.length > 1) {
			this._items.forEach(item => {
				if (item.isPoint) {
					allItems.points++;
				} else if (item.isPath) {
					allItems.paths++;
				} else if (item.isActivity) {
					allItems.activities++;
				}
			});

			let count = 0;

			if (allItems.points > 0) {
				this._dom.overview.appendChild(this._createOverviewItem("point", allItems.points, "pin", "gpx.content.pointsNum"));
				count++;
			}

			if (allItems.paths > 0) {
				this._dom.overview.appendChild(this._createOverviewItem("path", allItems.paths, "custom-distance", "gpx.content.pathsNum"));
				count++;
			}

			if (allItems.activities > 0) {
				this._dom.overview.appendChild(this._createOverviewItem("activity", allItems.activities, "walk", "gpx.content.activitiesNum"));
				count++;
			}

			this._dom.overview.classList.add(`count-${count}`);
		}
	}

	_createItems(gpxData: IImportResponse) {
		// naplnime vse dohromady
		const items: Array<{
			id: number;
			type: string;
			item: IImportTrack | any;
		}> = [];

		// data
		[{
			items: gpxData.data.points,
			type: "point",
		}, {
			items: gpxData.data.tracks,
			type: "path",
		}, {
			items: gpxData.data.routes || [],
			type: "route",
		}].forEach(categoryItem => {
			categoryItem.items.forEach(item => {
				items.push({
					id: item.sequenceId,
					type: categoryItem.type,
					item,
				});
			});
		});
		// seradime podle id
		items.sort((first, second) => first.id - second.id);
		// vytvorime
		items.forEach(item => {
			// otestujeme vuci diffu
			const diff = this._itemsDiff[item.id];

			// smazaneme skipneme
			if (diff?.removed) {
				return;
			}

			const gpxItem = new GPXItem({
				parent: this,
				type: item.type,
				data: item.item,
			});

			if (diff) {
				// aplikujeme diff
				gpxItem.diff = diff;
			}

			// pripneme dom
			this._dom.list.appendChild(gpxItem.container);
			// pridame do mapy
			this._layers.markers.addMarker(gpxItem.markers);
			// geometrie
			gpxItem.geometries.forEach(geom => {
				this._layers.geometry.addSmartGeometry(geom);
			});
			// data
			this._items.push(gpxItem);

			// pro zoom
			if (gpxItem.geometryCoords) {
				this._allZoomCoords = this._allZoomCoords.concat(gpxItem.geometryCoords);
			} else {
				gpxItem.coords.forEach(coor => {
					this._allZoomCoords.push(coor);
				});
			}
		});
	}

	_getSelectedItems() {
		return this._items.filter(item => item.checked);
	}

	_getSaveData() {
		// vytvorime data
		const items = [];
		const selected = this._getSelectedItems();

		// pripravime data
		selected.forEach(item => {
			items.push(item.exportData);
		});

		return items;
	}

	_createSaveDialog() {
		if (!this._getSelectedItems().length) {
			return;
		}

		const dialog = new GPXSave({
			folder: {
				id: this._folderId,
				title: this._folderTitle,
			},
			saveData: this._getSaveData(),
			onSave: folderId => {
				// zrusime data
				GPXImport.clear();
				this.clear();
				this.deactivate();
				// smazeme kes
				Mapy.ProviderMapyBox.removeFolderFromCache(folderId);
				// prejdeme do slozky
				if (Mapy.getComponent("mymaps").isActive()) {
					Mapy.getComponent("mymaps").deactivate();
				}
				Mapy.getComponent("places").state = { mid: folderId };
				Mapy.getComponent("mymaps").activate();
				Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("places"));
				Mapy.getComponent("dimcurator").setState();
				Mapy.getComponent("history").save();
			},
		});

		dialog.open();
	}

	_updateSelected() {
		const selected = this._getSelectedItems().length;

		this._dom.allRch.title.textContent = `${selected}/${this._items.length}`;
		this._dom.allRch.input.checked = selected === this._items.length;
	}

	_setSaveBtn() {
		// da se ukladat?
		const count = this._getSelectedItems().length;

		this._dom.saveBtn.disabled = !count;
	}

	_show3dRoutes() {
		if (Scene.isM3dActive() && this.isActive()) {
			const lines = [];

			this._items.forEach(item => {
				if (item.geometryCoords) {
					lines.push(item.geometryCoords.map(index => index.toWGS84()));
				}
			});

			if (lines.length) {
				this._layers.free = Scene.createLayerFree("gpx", lines, {
					lineColor: [255, 0, 0, 150],
					lineWidth: 5,
					lineOffset: [-0.19, 0, 0],
					outlineWidth: 0,
					outlineOffset: [-0.19, 0, 0],
				});
				Scene.addLayer(this._layers.free);
			}
		}
	}

	_removeFreeLayers() {
		if (this._layers.free) {
			Scene.removeLayer(this._layers.free);
			this._layers.free.destroy();
			this._layers.free = null;
		}
	}

	// eslint-disable-next-line class-methods-use-this
	_setCenterZoom(center, zoom) {
		if (zoom < 0 || zoom > 22) {
			return;
		}

		const [longitude, latitude] = center.toWGS84();

		store.dispatch(setCoordinates({
			longitude,
			latitude,
			animate: true,
		}));
		Mapy.getComponent("history").save(true);
	}
}
